import type { RootState } from '@/app/store'
import GoogleMap from '@/components/GoogleMap'
import Layout from '@/components/Layout'
import List from '@/components/List'
import Loading from '@/components/Loading'
import PdfMap from '@/components/PdfMap'
import SwiperGoogleMap from '@/components/SwiperGoogleMap'
import { currentCourse, isChangeCurrentCourse, spotCheck } from '@/features/spotMapSlice'
import { urlsheets } from '@/lib/custom'
import { apiGet } from '@/lib/originalFetchApi'
import styles from '@/styles/GoogleMap.module.scss'
import type { NextPage } from 'next'
import React, { useState, useEffect, Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import useSWR from 'swr'

const Page: NextPage = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [ListState, setListState] = useState<Api.SpotInSheetObject[]>()
  const [isAvailable, setAvailable] = useState(true)

  const spotCurrentPositionSelector = useSelector((state: RootState) => state.spotMap.currentPosition)
  const spotCheckStateSelector = useSelector((state: RootState) => state.spotMap.spotCheck)
  const spotWatchIdStateSelector = useSelector((state: RootState) => state.spotMap.watchId)

  const dataCampaign = useSelector((state: RootState) => state.apiAsync.campaign)

  const currentCourseId = useSelector((state: RootState) => state.spotMap.currentCourse)

  const isChangeCourse = useSelector((state: RootState) => state.spotMap.isChangeCurrentCourse)

  const { data: SheetsData, error: sheetsFetchError } = useSWR<Api.SheetsResBody>(`${urlsheets}`, apiGet, {
    revalidateOnFocus: false,
  })

  const sortSpots = (v1: Api.SpotInSheetObject, v2: Api.SpotInSheetObject): number => {
    if (v1.displayNumber < v2.displayNumber) {
      return -1
    } else if (v1.displayNumber > v2.displayNumber) {
      return 1
    } else {
      if (v1.id < v2.id) {
        return -1
      } else {
        return 1
      }
    }
  }

  useEffect(() => {
    if (!dataCampaign) return

    // listType
    // 1: MAP & リスト（デフォルト）
    // 2: MAPのみ
    // 3: リストのみ
    if (spotCheckStateSelector === 'map' && dataCampaign.listType === 3) {
      dispatch(spotCheck('list'))
    }
  }, [dataCampaign])

  useEffect(() => {
    if (!SheetsData) return
    if (!SheetsData.sheets) return
    if (!dataCampaign) return
    if (isChangeCourse) return
    if (!dataCampaign.defaultSheetId) return
    // 初期値のシートをセット
    dispatch(currentCourse(dataCampaign.defaultSheetId))
  }, [SheetsData, dataCampaign])

  useEffect(() => {
    if (!SheetsData) return
    if (!SheetsData.sheets) return

    const currentCourseSheet = SheetsData.sheets.find(({ id }) => id == currentCourseId)

    if (currentCourseSheet) setListState(currentCourseSheet.spots.sort(sortSpots))
    setAvailable(false)
  }, [SheetsData, currentCourseId])

  useEffect(() => {
    navigator.geolocation.clearWatch(spotWatchIdStateSelector)
  }, [spotCheckStateSelector])

  if (isAvailable) return <Loading />
  if (ListState === undefined) return <div>{''}</div>
  if (sheetsFetchError) return <div>{''}</div>
  if (!ListState) return <div>{''}</div>
  if (!SheetsData) return <div>{''}</div>

  return (
    <Layout title={t('spot.title', 'スポット一覧')}>
      <section>
        <div
          className={`${spotCheckStateSelector === 'list' ? 'c-contents relative pt-4 pb-10' : 'c-contents relative'}`}
        >
          {dataCampaign.listType === 1 && (
            <div
              className={`${styles.c_switch}
           ${spotCheckStateSelector === 'list' ? `${styles.isList}` : ''}
           ${spotCheckStateSelector === 'coupon' ? `${styles.isCoupon}` : ''}
           shadow-primary`}
            >
              <button
                type="button"
                onClick={() => {
                  dispatch(spotCheck('map'))
                }}
                className={`${styles.c_switch_button} ${spotCheckStateSelector !== 'map' ? 'text-[#8c8c8c]' : 'text-sub'}`}
              >
                {t('spot.map', 'マップ')}
              </button>
              <button
                type="button"
                onClick={() => {
                  dispatch(spotCheck('list'))
                }}
                className={`${styles.c_switch_button} ${spotCheckStateSelector !== 'list' ? 'text-[#8c8c8c]' : 'text-sub'}`}
              >
                {t('spot.list', 'リスト')}
              </button>

              {SheetsData.sheets?.some((sheet) => sheet.id === 'coupon' && sheet.spots) && (
                <button
                  type="button"
                  onClick={() => {
                    dispatch(spotCheck('coupon'))
                  }}
                  className={`${styles.c_switch_button} ${spotCheckStateSelector !== 'coupon' ? 'text-[#8c8c8c]' : 'text-sub'}`}
                >
                  {t('spot.coupon', 'クーポン')}
                </button>
              )}
            </div>
          )}

          {spotCheckStateSelector === 'list' && (
            <div className="relative mx-4 mt-8 flex items-center justify-between">
              <div className="flex items-center justify-between">
                <div className={`c-select ${styles.c_list_select} bg-[#fff] shadow-primary`}>
                  <select
                    className="border-none text-sm font-bold"
                    value={currentCourseId}
                    onChange={(e) => {
                      dispatch(currentCourse(e.target.value))
                      dispatch(isChangeCurrentCourse(true))
                    }}
                  >
                    {SheetsData.sheets?.map((v) => {
                      if (!v.spots) return
                      return (
                        <Fragment key={v.id}>
                          <option value={v.id}>{v.name}</option>
                        </Fragment>
                      )
                    })}
                  </select>
                </div>
              </div>
              <PdfMap
                maps={dataCampaign.maps}
                mapCaption={dataCampaign.mapCaption}
                style="c_illustration_map_pattern02"
              />
            </div>
          )}

          {spotCheckStateSelector === 'list' && (
            <div className="border-gray-200 c-shadow_top bg-white mt-3 border-t px-4 pb-6">
              {ListState?.map((spot, i) => {
                return (
                  <Fragment key={spot.id}>
                    <List
                      link="/spot/detail/"
                      query={`${spot.id}`}
                      title={spot.name}
                      address={spot.address}
                      state={spot.stamp === null ? '' : Number(spot.stamp.isHas)}
                      img={spot.image ? spot.image : ''}
                      course={spot.sheetNames || [t('spot_detail.other_recommendation', 'その他おすすめ')]}
                      isCoupon={spot.isCoupon}
                      stampBool={!!spot.stamp}
                      point={dataCampaign.type === 2 || dataCampaign.type === 3 ? spot.stamp?.point : undefined}
                    />
                  </Fragment>
                )
              })}
            </div>
          )}

          {spotCheckStateSelector === 'map' && (
            <>
              {spotCurrentPositionSelector ? (
                <div className={styles.c_overlay}>
                  <div className={styles.c_overlay_content}>
                    <div />
                    <div />
                    <div />
                  </div>
                </div>
              ) : (
                ''
              )}
              <div className={`relative ${styles.c_google_map_wrap}`}>
                {SheetsData.sheets?.map((sheet, idx) => {
                  if (sheet.id === 'coupon') {
                    if (sheet.spots) {
                      return (
                        <GoogleMap
                          key={sheet.id}
                          spotInSheet={ListState}
                          hasCoupon={true}
                          center={dataCampaign.center}
                        />
                      )
                    }
                    return (
                      <GoogleMap
                        key={sheet.id}
                        spotInSheet={ListState}
                        hasCoupon={false}
                        center={dataCampaign.center}
                      />
                    )
                  }
                })}

                <div className={`${styles.c_google_map_select}`}>
                  <div className={`c-select ${styles.c_google_select}`}>
                    <select
                      className="border-none text-xs font-bold"
                      value={currentCourseId}
                      onChange={(e) => {
                        dispatch(currentCourse(e.target.value))
                        dispatch(isChangeCurrentCourse(true))
                      }}
                    >
                      {SheetsData.sheets.map((v) => {
                        if (!v.spots) return

                        return (
                          <Fragment key={v.id}>
                            <option value={v.id}>{v.name}</option>
                          </Fragment>
                        )
                      })}
                    </select>
                  </div>
                </div>
                <PdfMap maps={dataCampaign.maps} mapCaption={dataCampaign.mapCaption} style="c_illustration_map" />
                <SwiperGoogleMap spotInSheet={ListState} />
              </div>
            </>
          )}

          {/* クーポン一覧 */}
          {spotCheckStateSelector === 'coupon' && (
            <>
              <div className="border-gray-200 c-shadow_top bg-white mt-14 border-t px-4 pb-6">
                {SheetsData.sheets[0].spots?.map((spot, i) => {
                  if (spot.isCoupon) {
                    return (
                      <Fragment key={spot.id}>
                        <List
                          link="/spot/detail/"
                          query={`${spot.id}`}
                          title={spot.name}
                          address={spot.address}
                          state={spot.stamp === null ? '' : Number(spot.stamp.isHas)}
                          img={spot.image ? spot.image : ''}
                          course={spot.sheetNames || [t('spot_detail.other_recommendation', 'その他おすすめ')]}
                          isCoupon={spot.isCoupon}
                          stampBool={!!spot.stamp}
                          point={dataCampaign.type === 2 || dataCampaign.type === 3 ? spot.stamp?.point : undefined}
                        />
                      </Fragment>
                    )
                  }
                })}
              </div>
            </>
          )}
        </div>
      </section>
    </Layout>
  )
}

export default Page
